import { FontIcon, IPanelStyles, IStackItemStyles, IStackStyles, IStackTokens, Label, Link, mergeStyleSets, Panel, PanelType, PrimaryButton, ProgressIndicator, ScrollablePane, ScrollbarVisibility, Stack } from "@fluentui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { BackgroundTaskStatusEnum } from "../../src/taskpane/components/enums/BackgroundTaskStatusEnum";
import { ContentManagerCopyMoveModeEnum } from "../../src/taskpane/components/enums/ContentManagerCopyMoveModeEnum";
import { Fileview } from "../../src/taskpane/components/_v2/components/Explorer/Fileview/Fileview";
import { RootState } from "../../src/taskpane/redux";
import { setContentManagerExchangeProgress, setContentManagerMoveTo, setContentManagerMoveToError } from "../../src/taskpane/redux/modules/contentManagerDirectory";
import { isMobile } from "../../src/taskpane/styles/mobileStyles";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { setIsBusySaving } from "../../src/taskpane/redux/modules/savingInBackground";
import { CopyMoveErrors } from "../../src/taskpane/config/constants";

const classNames = mergeStyleSets({
    wrapper: {
        height: "calc(100vh - 140px)", //subtract header and breadcrumb heigths
        position: "relative",
    }
});

const panelStyles: Partial<IPanelStyles> = {
    main: {
        overflow: "hidden"
    },
    content: {
        height: "calc(100vh - 124px);",
        paddingLeft: 5,
        paddingRight: 5,
        paddingBottom: 0
    }
};

const containerStackTokens: IStackTokens = {
    childrenGap: 5
};

const containerStackStyles: IStackStyles = {
    root: {
        paddingTop: 15,
        paddingLeft: 10,
        paddingRight: 10,
        height: "100%",
    }
};

const metadataStackItemStyles: IStackItemStyles = {
    root: {
        overflowY: "auto"
    }
};

export interface ICMMoveFilesListPanelProps {
    closePanel?: () => void;
    panelIsOpen: boolean;
}

export const CMMoveFilesListPanel = ({ panelIsOpen, closePanel }: ICMMoveFilesListPanelProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const DISPLAY_ITEM_AMOUNT = 15;

    const dispatch = useDispatch();

    const moveToSelectedFiles = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.selectedFiles);
    const moveToMode = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.mode);
    const moveTo = useSelector((state: RootState) => state.contentManagerDirectory.moveTo);
    const moveToProgressCountCompleted = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.progressCountCompleted);
    const moveToProgressCountTotal = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.progressCountTotal);
    const moveToError = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.error);
    const moveToCopyJobResults = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.copyJobResults);

    const exchangeProgress = useSelector((state: RootState) => state.contentManagerDirectory.exchange.progress);
    const errorMessages = useSelector((state: RootState) => state.savingInBackground.errorMessages);

    const [fileViews, setFileViews] = React.useState<JSX.Element[]>([]);
    const [percentComplete, setPercentComplete] = React.useState(0);
    const [filesCompleted, setFilesCompleted] = React.useState(0);
    const [showMoreExpanded, setShowMoreExpanded] = React.useState(false);

    const [hasError, setHasError] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState("");

    React.useEffect(() => {
        if (moveToError || errorMessages?.length > 0) {
            setHasError(true);

            let errorMsg: string = moveToError ? moveToError : errorMessages[0];
            if (errorMsg === CopyMoveErrors.DuplicateFilenames) {
                errorMsg = languageStrings.MultipleFilesFoldersCopyMove;
            }
            setErrorMessage(errorMsg);
        }
    }, [moveToError, errorMessages])

    React.useEffect(() => {
        if (moveToSelectedFiles?.length > 0) {
            setFilesCompleted(0);
            const filesToMove = [...moveToSelectedFiles];
            setFileViews(moveToSelectedFiles.filter(x => x.isBusy).slice(0, showMoreExpanded ? filesToMove.length : DISPLAY_ITEM_AMOUNT).map((file, index) => {
                return (<div key={index}>
                    <Fileview
                        item={{ ...file, driveId: '', fileType: file.extension }}
                        isMobile={isMobile()}
                        multiSelect={false}
                        hasShowMore={false}
                        tenantEnablePII={false}
                        isLocalFile={true}
                        isBusy={file.isBusy}
                    />
                </div>)
            }));

            const filesCompletedLocal = moveToSelectedFiles.filter(x => !x.isBusy).length;
            setFilesCompleted(filesCompletedLocal);
            setPercentComplete(filesCompletedLocal / moveToSelectedFiles?.length);
        }
        else if (moveToMode === ContentManagerCopyMoveModeEnum.MoveAll || moveToMode === ContentManagerCopyMoveModeEnum.CopyAll) {
            setPercentComplete(moveToProgressCountCompleted / moveToProgressCountTotal);
        }
    }, [moveToSelectedFiles, showMoreExpanded, moveToCopyJobResults, moveTo.busyMovingFiles, moveTo.busyFetchingFiles, moveToProgressCountCompleted, moveToProgressCountTotal]);

    const getPanelHeaderText = () => {
        if (moveToError) {
            return "";
        }
        if (exchangeProgress && exchangeProgress.status !== undefined) {
            if (exchangeProgress.status !== BackgroundTaskStatusEnum.Completed && exchangeProgress.status !== BackgroundTaskStatusEnum.Failure) {
                return `${languageStrings.SavingFiles} ${`${exchangeProgress.message ? `(${exchangeProgress.message})` : ""}`}`;
            } else {
                return `${exchangeProgress.message}`;
            }
        }
        if (moveTo.copyJobResults?.length > 0 || moveTo.isMoveInProgress) {
            return (moveToMode === ContentManagerCopyMoveModeEnum.Move || moveToMode === ContentManagerCopyMoveModeEnum.MoveAll) ? languageStrings.MoveProgress : languageStrings.CopyProgress;
        }

        if (moveToMode === ContentManagerCopyMoveModeEnum.Move || moveToMode === ContentManagerCopyMoveModeEnum.MoveAll) {
            return `${moveTo.progressCountTotal} ${moveTo.progressCountTotal === 1 ? languageStrings.ItemToMove.toLowerCase() : languageStrings.ItemsToMove.toLowerCase()}`;
        } else {
            return `${moveTo.progressCountTotal} ${moveTo.progressCountTotal === 1 ? languageStrings.ItemToCopy.toLowerCase() : languageStrings.ItemsToCopy.toLowerCase()}`;
        }
    }

    const getProgressCounterDescription = () => {
        let returnString = "";

        if (moveTo.busyFetchingFiles) {
            if (moveToMode === ContentManagerCopyMoveModeEnum.CopyAll || moveToMode === ContentManagerCopyMoveModeEnum.MoveAll) {
                if (!moveTo.isPreparingJobs) {
                    returnString = languageStrings.FetchProgressCounterAll;
                }
                else {
                    returnString = moveToMode === ContentManagerCopyMoveModeEnum.CopyAll
                        ? languageStrings.PreparingCopy
                        : languageStrings.PreparingMove;
                }
            } else {
                returnString = languageStrings.FetchProgressCounter;
            }
        } else {
            returnString = (moveToMode === ContentManagerCopyMoveModeEnum.Move || moveToMode === ContentManagerCopyMoveModeEnum.MoveAll) ? languageStrings.MoveProgressCounter : languageStrings.CopyProgressCounter;
        }

        if (moveToSelectedFiles?.length > 0) {
            return returnString.replace("{completed}", filesCompleted.toString()).replace("{total}", moveToSelectedFiles.length.toString());
        }

        return returnString.replace("{completed}", moveToProgressCountCompleted.toString()).replace("{total}", moveToProgressCountTotal.toString());
    }

    const cancelMove = () => {
        dispatch(setContentManagerMoveTo({
            isMoveCancelled: true,
            busyFetchingFiles: false,
            moveToEnabled: false,
            workingDirectories: [],
            selectedFiles: [],
            siteUrl: "",
            progressCountCompleted: 0,
            progressCountTotal: 0,
            sourceServerRelPath: "",
        }));
        dispatch(setContentManagerExchangeProgress(undefined));
        closePanel();
    }

    const onRenderFooterContent = React.useCallback(
        () => (
            <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 15 }}>
                <PrimaryButton
                    iconProps={{ iconName: 'Checkmark' }}
                    onClick={() => {
                        if (errorMessages?.length > 0) {
                            dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: false }));
                        }
                        if (moveToError) {
                            dispatch(setContentManagerMoveToError(""));
                        }
                        closePanel();
                    }}
                    text={languageStrings.Close}
                />
            </Stack>
        ),
        [],
    );

    return (
        <Panel
            headerText={getPanelHeaderText()}
            isOpen={panelIsOpen}
            onDismiss={() => {
                if (errorMessages?.length > 0) {
                    dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: false }));
                }
                if (moveToError) {
                    dispatch(setContentManagerMoveToError(""));
                }
                closePanel()
            }}
            isLightDismiss={false}
            closeButtonAriaLabel="Close"
            type={PanelType.custom}
            customWidth={'350px'}
            styles={panelStyles}
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
        >
            <>
                {hasError &&
                    <div style={{ marginTop: 75, padding: 5 }} className="centerText">
                        <Label>{errorMessage}</Label>
                    </div>
                }
                {(!exchangeProgress || exchangeProgress?.status === undefined) && !hasError &&
                    <div className={classNames.wrapper}>
                        <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                            <Stack>
                                <Stack tokens={containerStackTokens} styles={containerStackStyles}>
                                    <Stack.Item grow styles={metadataStackItemStyles}>
                                        <div className="xb-content">
                                            {!moveTo.busyFetchingFiles && moveTo.isMoveInProgress && <ProgressIndicator styles={{ root: { marginBottom: 10 } }} description={getProgressCounterDescription()} percentComplete={percentComplete} />}
                                            {fileViews}
                                            {moveToSelectedFiles?.filter(x => x.isBusy)?.length > DISPLAY_ITEM_AMOUNT && <div className="xb-show-more-action" style={{ marginTop: 10, marginLeft: 4 }} onClick={() => { setShowMoreExpanded(prev => !prev) }}>
                                                <FontIcon iconName={!showMoreExpanded ? "ChevronDown" : "ChevronUp"} />
                                                <span>{!showMoreExpanded ? languageStrings.ShowMore : languageStrings.ShowLess}</span>
                                            </div>}
                                            {moveTo.busyFetchingFiles && <Label disabled>{getProgressCounterDescription()}</Label>}
                                            {moveTo.busyFetchingFiles && !moveTo.isPreparingJobs && <Link onClick={() => cancelMove()}>{languageStrings.Cancel}</Link>}
                                        </div>
                                    </Stack.Item>
                                </Stack>
                            </Stack>
                        </ScrollablePane>
                    </div>
                }
                {exchangeProgress && exchangeProgress.status !== undefined && !hasError &&
                    <div className={classNames.wrapper}>
                        <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                            <Stack tokens={{ childrenGap: 2 }} styles={containerStackStyles}>
                                {exchangeProgress.logs?.map((log, index) => {
                                    return (
                                        <Label key={index} disabled>{log}</Label>
                                    )
                                })}
                            </Stack>
                        </ScrollablePane>
                    </div>
                }
            </>
        </Panel>
    )
};